import { zerlege, type Datum } from './datum'

const TAG_MS = 86400000

/**
 * Verbindet ein Datum mit einer Uhrzeit IN EINER ZEITZONE und liefert den
 * Zeitpunkt.
 *
 * ⚠️ Das ist die einzige Stelle, an der Zeitzonen vorkommen. Die Expansion
 * rechnet auf reinen Datumswerten; erst hier entsteht ein Zeitpunkt. Nur so
 * bleibt „jeden Montag 8:00" ueber die Zeitumstellung hinweg 8:00 Ortszeit -
 * in UTC gerechnet waere es danach 7:00.
 *
 * An einer Umstellungsgrenze ist die Wanduhrzeit nicht eindeutig:
 *
 * - Uebersprungene Stunde (Fruehjahr): 2:30 gibt es am Umstellungstag nicht.
 *   Der Zeitpunkt landet auf 3:30 Ortszeit statt auszufallen - ein Termin,
 *   der verschwindet, waere der schlimmere Fehler.
 * - Doppelte Stunde (Herbst): 2:30 gibt es zweimal. Genommen wird die ERSTE
 *   Auslegung, damit der Termin nicht rueckwaerts wandert.
 */
export function zeitInZone(datum: Datum, uhrzeit: string, zone: string): Date {
  const wandUhr = alsWandUhr(datum, uhrzeit)

  // Die Versaetze eines Tages VOR und eines Tages NACH der Wanduhrzeit. Eine
  // Umstellung liegt garantiert dazwischen, und beide Kandidaten sind damit
  // bekannt, ohne zu iterieren.
  const davor = versatz(wandUhr - TAG_MS, zone)
  const danach = versatz(wandUhr + TAG_MS, zone)

  const frueh = wandUhr - davor
  const spaet = wandUhr - danach

  // `frueh` zuerst: bei doppelter Stunde sind beide gueltig und `frueh` ist
  // die erste Auslegung; in der uebersprungenen Stunde ist keiner gueltig und
  // `frueh` ist genau der um die Luecke nach VORNE geschobene Zeitpunkt.
  if (versatz(frueh, zone) === davor) return new Date(frueh)
  if (versatz(spaet, zone) === danach) return new Date(spaet)
  return new Date(frueh)
}

/**
 * Die Umkehrung von `zeitInZone`: welches Datum und welche Uhrzeit zeigt die
 * Wanduhr in dieser Zone zu diesem Zeitpunkt.
 *
 * Gebraucht, damit eine Serie weiss, dass sie „8:00" meint und nicht
 * „07:00 UTC" - nach der Zeitumstellung sind das verschiedene Dinge.
 */
export function inZone(zeitpunkt: Date, zone: string): { datum: Datum; uhrzeit: string } {
  const w = felder(zeitpunkt.getTime(), zone)
  const zwei = (n: number): string => String(n).padStart(2, '0')
  return {
    datum: `${String(w('year')).padStart(4, '0')}-${zwei(w('month', 1))}-${zwei(w('day', 1))}`,
    uhrzeit: `${zwei(w('hour'))}:${zwei(w('minute'))}`,
  }
}

function alsWandUhr(datum: Datum, uhrzeit: string): number {
  const { jahr, monat, tag } = zerlege(datum)
  const teile = uhrzeit.split(':')
  const stunde = Number(teile[0])
  const minute = Number(teile[1] ?? '0')
  if (!Number.isInteger(stunde) || !Number.isInteger(minute)) {
    throw new Error(`Keine gültige Uhrzeit: ${uhrzeit}`)
  }
  return Date.UTC(jahr, monat - 1, tag, stunde, minute, 0, 0)
}

const FELDER = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
} as const

/** Die Wanduhr-Felder der Zone zu einem Zeitpunkt, als Nachschlagefunktion. */
function felder(zeitpunkt: number, zone: string): (name: string, vorgabe?: number) => number {
  let teile
  try {
    teile = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      ...FELDER,
    }).formatToParts(new Date(zeitpunkt))
  } catch {
    // ⚠️ Nicht still auf UTC zurueckfallen: eine falsch geschriebene Zone
    // wuerde sonst monatelang unbemerkt um Stunden danebenliegen.
    throw new Error(`Unbekannte Zeitzone: ${zone}`)
  }
  const w = new Map<string, number>()
  for (const p of teile) if (p.type !== 'literal') w.set(p.type, Number(p.value))
  return (name, vorgabe = 0) => w.get(name) ?? vorgabe
}

/** Versatz der Zone gegenueber UTC in Millisekunden, zum gegebenen Zeitpunkt. */
function versatz(zeitpunkt: number, zone: string): number {
  const feld = felder(zeitpunkt, zone)
  const alsUtc = Date.UTC(
    feld('year'),
    feld('month', 1) - 1,
    feld('day', 1),
    feld('hour'),
    feld('minute'),
    feld('second'),
  )
  return alsUtc - zeitpunkt
}
